// src/components/ListasTrabajo/GestionListas/ListaCard.js

import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    IconButton,
    Chip,
    Box,
    LinearProgress,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Folder as FolderIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    MoreVert as MoreVertIcon,
    ContentCopy as DuplicarIcon,
    Star,
    StarBorder,
    Share as ShareIcon,
    Person as PersonIcon,
} from '@mui/icons-material';

import { formatDotNetDate } from '../../../utils/dateHelpers';

const ListaCard = ({ lista, onEditar, onEliminar, onVerDetalle, onDuplicar, onToggleFavorita, onCompartir, vistaCompacta, compartida }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const porcentajeVisto = lista.Total_Expedientes > 0
        ? Math.round((lista.Expedientes_Vistos / lista.Total_Expedientes) * 100)
        : 0;

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuAction = (action) => (e) => {
        e?.stopPropagation?.();
        handleMenuClose();
        action();
    };

    const progressColor = porcentajeVisto === 100 ? '#4caf50' : '#ff9800';

    const menuComponent = (
        <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            slotProps={{ paper: { sx: { borderRadius: '4px' } } }}
        >
            {onEditar && (
                <MenuItem onClick={handleMenuAction(() => onEditar(lista))}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
            )}
            <MenuItem onClick={handleMenuAction(() => onDuplicar?.(lista))}>
                <ListItemIcon><DuplicarIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Duplicar</ListItemText>
            </MenuItem>
            {onCompartir && !compartida && (
                <MenuItem onClick={handleMenuAction(() => onCompartir(lista))}>
                    <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Compartir</ListItemText>
                </MenuItem>
            )}
            <MenuItem onClick={handleMenuAction(() => onToggleFavorita?.(lista))}>
                <ListItemIcon>
                    {lista.Favorita ? <Star fontSize="small" sx={{ color: '#f9a825' }} /> : <StarBorder fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{lista.Favorita ? 'Quitar favorita' : 'Favorita'}</ListItemText>
            </MenuItem>
            {onEliminar && (
                <MenuItem onClick={handleMenuAction(() => onEliminar(lista.Id))} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Eliminar</ListItemText>
                </MenuItem>
            )}
        </Menu>
    );

    // ─── List mode (vistaCompacta === false): compact horizontal row ───
    if (!vistaCompacta) {
        return (
            <>
                <Card
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '4px',
                        px: 2,
                        py: 0.5,
                        minHeight: 64,
                        maxHeight: 70,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: 6,
                            bgcolor: 'action.hover',
                        },
                    }}
                    onClick={() => onVerDetalle(lista)}
                >
                    {/* Folder icon with color */}
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${lista.Color}20`,
                            mr: 2,
                            flexShrink: 0,
                        }}
                    >
                        <FolderIcon sx={{ color: lista.Color, fontSize: 24 }} />
                    </Box>

                    {/* Favorite star */}
                    <Tooltip title={lista.Favorita ? 'Quitar favorita' : 'Marcar favorita'}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorita?.(lista);
                            }}
                            sx={{ mr: 1, flexShrink: 0 }}
                        >
                            {lista.Favorita
                                ? <Star sx={{ color: '#f9a825', fontSize: 20 }} />
                                : <StarBorder sx={{ fontSize: 20, color: 'text.disabled' }} />
                            }
                        </IconButton>
                    </Tooltip>

                    {/* Name + description */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                            {lista.Nombre}
                        </Typography>
                        {lista.Descripcion && (
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                                {lista.Descripcion}
                            </Typography>
                        )}
                    </Box>

                    {/* Stats inline */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2, flexShrink: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {lista.Total_Expedientes || 0}
                            </Box>
                            {' total'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <Box component="span" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {lista.Expedientes_Vistos || 0}
                            </Box>
                            {' vistos'}
                        </Typography>
                    </Box>

                    {/* Progress chip */}
                    <Chip
                        label={`${porcentajeVisto}%`}
                        size="small"
                        sx={{
                            bgcolor: progressColor,
                            color: 'white',
                            fontWeight: 600,
                            mr: 1,
                            flexShrink: 0,
                            minWidth: 50,
                        }}
                    />

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Tooltip title="Ver expedientes">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onVerDetalle(lista);
                                }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={handleMenuOpen}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Card>
                {menuComponent}
            </>
        );
    }

    // ─── Grid/card mode (vistaCompacta === true): vertical card layout ───
    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    borderRadius: '4px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 8,
                    },
                    cursor: 'pointer',
                }}
                onClick={() => onVerDetalle(lista)}
            >
                <CardContent sx={{ flexGrow: 1, p: 1.5, '&:last-child': { pb: 1 } }}>
                    {/* Header row: icon + name + star + menu */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${lista.Color}20`,
                                mr: 1,
                                flexShrink: 0,
                            }}
                        >
                            <FolderIcon sx={{ color: lista.Color, fontSize: 22 }} />
                        </Box>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                            {lista.Nombre}
                        </Typography>
                        <Tooltip title={lista.Favorita ? 'Quitar favorita' : 'Marcar favorita'}>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); onToggleFavorita?.(lista); }}
                                sx={{ flexShrink: 0, p: 0.5 }}
                            >
                                {lista.Favorita
                                    ? <Star sx={{ color: '#f9a825', fontSize: 18 }} />
                                    : <StarBorder sx={{ fontSize: 18, color: 'text.disabled' }} />
                                }
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e); }} sx={{ flexShrink: 0, p: 0.5 }}>
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>

                    {/* Date + owner */}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: '44px', display: 'block', mb: 0.5 }}>
                        {compartida
                            ? <><PersonIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: 'middle' }} />{lista.Usuario}</>
                            : `Creada: ${formatDotNetDate(lista.Fecha_Creacion, 'dd/MM/yyyy')}`
                        }
                    </Typography>

                    {/* Description */}
                    {lista.Descripcion && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 0.5,
                            }}
                        >
                            {lista.Descripcion}
                        </Typography>
                    )}

                    {/* Stats row + progress inline */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                        <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700 }}>{lista.Total_Expedientes || 0}</Box>
                            <Box component="span" sx={{ color: 'text.secondary', fontSize: 11 }}> total</Box>
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" sx={{ fontWeight: 700, color: '#4caf50' }}>{lista.Expedientes_Vistos || 0}</Box>
                            <Box component="span" sx={{ color: 'text.secondary', fontSize: 11 }}> vistos</Box>
                        </Typography>
                        <Chip
                            label={`${porcentajeVisto}%`}
                            size="small"
                            sx={{ bgcolor: progressColor, color: 'white', fontWeight: 600, height: 20, fontSize: 11 }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Ver expedientes">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onVerDetalle(lista); }}>
                                <VisibilityIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Progress bar */}
                    {lista.Total_Expedientes > 0 && (
                        <LinearProgress
                            variant="determinate"
                            value={porcentajeVisto}
                            sx={{
                                mt: 0.5,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': { borderRadius: 2 },
                            }}
                        />
                    )}
                </CardContent>
            </Card>
            {menuComponent}
        </>
    );
};

export default ListaCard;
